export interface LaneConfig {
  type: 'safe' | 'road' | 'water' | 'goal';
  speed: number;
}

export class LaneManager {
  private laneConfigs: LaneConfig[];
  private gridSize: number;
  private gridCols: number;

  constructor(laneConfigs: LaneConfig[], gridSize: number, gridCols: number) {
    this.laneConfigs = laneConfigs;
    this.gridSize = gridSize;
    this.gridCols = gridCols;
  }

  public reset(): void {
    // LaneManager doesn't need to reset any state currently
    // This method exists for consistency with other managers
  }

  public getLaneType(row: number): string {
    if (row < 0 || row >= this.laneConfigs.length) {
      return 'safe'; // Default to safe for out of bounds
    }
    
    // Convert row to config index (configs are ordered from bottom to top)
    const configIndex = this.laneConfigs.length - 1 - row;
    return this.laneConfigs[configIndex]?.type || 'safe';
  }

  public getLaneSpeed(row: number): number {
    if (row < 0 || row >= this.laneConfigs.length) {
      return 0; // No speed for out of bounds
    }
    
    // Convert row to config index
    const configIndex = this.laneConfigs.length - 1 - row;
    return this.laneConfigs[configIndex]?.speed || 0;
  }

  public isSafeLane(row: number): boolean {
    const laneType = this.getLaneType(row);
    return laneType === 'safe' || laneType === 'goal';
  }

  public isRoadLane(row: number): boolean {
    return this.getLaneType(row) === 'road';
  }

  public isWaterLane(row: number): boolean {
    return this.getLaneType(row) === 'water';
  }

  public isGoalLane(row: number): boolean {
    return this.getLaneType(row) === 'goal';
  }

  public getLaneColor(row: number): string {
    const laneType = this.getLaneType(row);
    
    switch (laneType) {
      case 'safe':
        return '#00AA00'; // Green
      case 'road':
        return '#444444'; // Dark gray
      case 'water':
        return '#0066CC'; // Blue
      case 'goal':
        return '#00AA00'; // Green
      default:
        return '#000000'; // Black fallback
    }
  }

  public getRowFromPosition(y: number): number {
    return Math.floor(y / this.gridSize);
  }

  public getColumnFromPosition(x: number): number {
    return Math.floor(x / this.gridSize);
  }

  public getPositionFromGrid(row: number, col: number): { x: number, y: number } {
    return {
      x: col * this.gridSize,
      y: row * this.gridSize
    };
  }

  public isValidPosition(row: number, col: number): boolean {
    return row >= 0 && row < this.laneConfigs.length && col >= 0 && col < this.gridCols;
  }

  public getLaneCount(): number {
    return this.laneConfigs.length;
  }

  public getGridSize(): number {
    return this.gridSize;
  }

  public getGridCols(): number {
    return this.gridCols;
  }

  // Get all road lanes (for spawning vehicles)
  public getRoadLanes(): number[] {
    const roadLanes: number[] = [];
    
    for (let row = 0; row < this.laneConfigs.length; row++) {
      if (this.isRoadLane(row)) {
        roadLanes.push(row);
      }
    }
    
    return roadLanes;
  }

  // Get all water lanes (for spawning logs/turtles)
  public getWaterLanes(): number[] {
    const waterLanes: number[] = [];
    
    for (let row = 0; row < this.laneConfigs.length; row++) {
      if (this.isWaterLane(row)) {
        waterLanes.push(row);
      }
    }
    
    return waterLanes;
  }

  // Get difficulty multiplier for a given level
  public getDifficultyMultiplier(level: number): number {
    // Increase speed and reduce gaps as levels progress
    return 1 + (level - 1) * 0.15; // 15% increase per level
  }

  // Get adjusted speed for a lane based on difficulty
  public getAdjustedLaneSpeed(row: number, level: number): number {
    const baseSpeed = this.getLaneSpeed(row);
    const multiplier = this.getDifficultyMultiplier(level);
    return baseSpeed * multiplier;
  }
}